import { memo } from 'react';

import BigScreen from '@src/components/VideosContainer/screen/BigScreen';
import VideoBox from '@src/components/VideoBox';
import { WhiteboardReadOnly } from '@src/components/Whiteboard';
import { IVideoStream, TLayoutItems, TVideoObjectFit } from '@src/types';

import PipCompactAvatar from './PipCompactAvatar';

const getVideoKey = (video: { peerId: string; type: string }) => `${video.peerId}-${video.type}`;

interface IPipCompactPinnedLayoutProps {
  video: IVideoStream;
  pipWindowHeight: number;
  minFullsizeHeightPx: number;
  getObjectFit: (type: string) => TVideoObjectFit;
}

export const PipCompactPinnedLayout = memo(function PipCompactPinnedLayout({
  video,
  pipWindowHeight,
  minFullsizeHeightPx,
  getObjectFit,
}: IPipCompactPinnedLayoutProps) {
  // Compact mode kicks in at 260px
  const COMPACT_THRESHOLD_PX = 260;

  // Full size above 260px, scale down below that
  let heightScale = 1;
  if (pipWindowHeight < COMPACT_THRESHOLD_PX) {
    // Scale from 1 to 0 between COMPACT_THRESHOLD_PX and 0
    heightScale = Math.max(0, Math.min(1, pipWindowHeight / minFullsizeHeightPx));
  }

  return (
    <div
      className="pip-compact-layout"
      style={{
        transform: `scale(${heightScale})`,
        opacity: heightScale,
        transition: 'transform 180ms linear, opacity 180ms linear',
      }}
    >
      <div style={{ width: '100%', height: '100%' }}>
        <VideoBox {...video} isPipBox objectFit={getObjectFit(video.type)} />
      </div>
    </div>
  );
});

interface IPipSingleLayoutProps {
  mainVideo?: IVideoStream;
  selectedLayout?: TLayoutItems;
}

export const PipSingleLayout = memo(function PipSingleLayout({ mainVideo, selectedLayout }: IPipSingleLayoutProps) {
  return (
    <div className="pip-single-layout">
      <div className="pip-video-wrapper">
        <BigScreen
          isPipBox
          isWhiteboardActive={false}
          whiteboardWidth="full"
          videoBox={mainVideo}
          selectedLayout={selectedLayout}
        />
      </div>
    </div>
  );
});

interface IPipHorizontalLayoutProps {
  displayedVideos: IVideoStream[];
  leavingVideoIds: Set<string>;
  getObjectFit: (type: string) => TVideoObjectFit;
}

export const PipHorizontalLayout = memo(function PipHorizontalLayout({
  displayedVideos,
  leavingVideoIds,
  getObjectFit,
}: IPipHorizontalLayoutProps) {
  return (
    <div className={`pip-horizontal-layout ${displayedVideos.length === 2 ? 'two-columns' : ''}`}>
      {displayedVideos.map((video) => {
        const videoKey = getVideoKey(video);
        const isLeaving = leavingVideoIds.has(videoKey);

        return (
          <div key={videoKey} className={`pip-video-box ${isLeaving ? 'is-leaving' : ''}`}>
            <VideoBox {...video} isPipBox objectFit={getObjectFit(video.type)} />
          </div>
        );
      })}
    </div>
  );
});

interface IPipGridLayoutProps {
  displayedVideos: IVideoStream[];
  leavingVideoIds: Set<string>;
  getObjectFit: (type: string) => TVideoObjectFit;
}

export const PipGridLayout = memo(function PipGridLayout({
  displayedVideos,
  leavingVideoIds,
  getObjectFit,
}: IPipGridLayoutProps) {
  if (displayedVideos.length === 2) {
    return (
      <div className="pip-grid-layout two-rows">
        {displayedVideos.map((video) => {
          const videoKey = getVideoKey(video);
          const isLeaving = leavingVideoIds.has(videoKey);

          return (
            <div key={videoKey} className={`pip-video-box ${isLeaving ? 'is-leaving' : ''}`}>
              <VideoBox {...video} isPipBox objectFit={getObjectFit(video.type)} />
            </div>
          );
        })}
      </div>
    );
  }

  const [topVideo, ...bottomVideos] = displayedVideos;
  const topVideoKey = topVideo ? getVideoKey(topVideo) : null;

  return (
    <div className="pip-grid-layout">
      <div className="pip-grid-top">
        {topVideo && topVideoKey && (
          <div className={`pip-video-box ${leavingVideoIds.has(topVideoKey) ? 'is-leaving' : ''}`}>
            <VideoBox {...topVideo} isPipBox objectFit={getObjectFit(topVideo.type)} />
          </div>
        )}
      </div>
      <div className="pip-grid-bottom">
        {bottomVideos.map((video) => {
          const videoKey = getVideoKey(video);
          const isLeaving = leavingVideoIds.has(videoKey);

          return (
            <div key={videoKey} className={`pip-video-box ${isLeaving ? 'is-leaving' : ''}`}>
              <VideoBox {...video} isPipBox objectFit={getObjectFit(video.type)} />
            </div>
          );
        })}
      </div>
    </div>
  );
});

interface IPipCompactAvatarsLayoutProps {
  videos: IVideoStream[];
  pipWindowWidth: number;
  pipWindowHeight: number;
  leavingVideoIds: Set<string>;
  minFullsizeHeightPx: number;
}

export const PipCompactAvatarsLayout = memo(function PipCompactAvatarsLayout({
  videos,
  pipWindowWidth,
  pipWindowHeight,
  leavingVideoIds,
  minFullsizeHeightPx,
}: IPipCompactAvatarsLayoutProps) {
  const isNarrowCompactWidth = pipWindowWidth < 350;
  const isExtraNarrowCompactWidth = pipWindowWidth < 270;

  // Within compact mode, start shrinking avatars below this height down to 0 at height 0.
  const heightScale = Math.max(0, Math.min(1, pipWindowHeight / minFullsizeHeightPx));

  // Priority / placement:
  // - Center (index 1) shows the 1st priority video
  // - Left (index 0) shows the 2nd priority video
  // - Right (index 2) shows the 3rd priority video
  const orderedVideos = videos.length === 3 ? [videos[1], videos[0], videos[2]] : videos;

  return (
    <div className="pip-compact-layout">
      <div
        className={`pip-compact-avatars ${isNarrowCompactWidth ? 'is-narrow' : ''} ${isExtraNarrowCompactWidth ? 'is-overlap' : ''}`}
      >
        {orderedVideos.map((video, idx) => {
          const baseScale = orderedVideos.length === 3 ? (idx === 1 ? 1 : 0.85) : 1;
          const scale = baseScale * heightScale;
          const videoKey = getVideoKey(video);
          const isLeaving = leavingVideoIds.has(videoKey);

          return (
            <div key={videoKey} className={`pip-avatar-wrapper ${isLeaving ? 'is-leaving' : ''}`}>
              <PipCompactAvatar
                id={video.id}
                type={video.type}
                stream={video.stream}
                poster={video.poster}
                peerId={video.peerId}
                muted={video.muted}
                audioVolume={video.audioVolume}
                scale={scale}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

interface IPipLayerStackProps {
  showNormalLayer: boolean;
  showCompactLayer: boolean;
  normalLayerVisible: boolean;
  compactLayerVisible: boolean;
  normalContent: React.ReactNode;
  compactContent: React.ReactNode;
}

interface IPipWhiteboardLayoutProps {
  pipWindowHeight: number;
  sortedVideos: IVideoStream[];
  getObjectFit: (type: string) => TVideoObjectFit;
  leavingVideoIds: Set<string>;
}

const PIP_WHITEBOARD_WITH_TILES_MIN_HEIGHT = 400;
const MAX_WHITEBOARD_TILES = 2;

export const PipWhiteboardLayout = memo(function PipWhiteboardLayout({
  pipWindowHeight,
  sortedVideos,
  getObjectFit,
  leavingVideoIds,
}: IPipWhiteboardLayoutProps) {
  const showTiles = pipWindowHeight > PIP_WHITEBOARD_WITH_TILES_MIN_HEIGHT;
  const tileVideos = showTiles ? sortedVideos.slice(0, MAX_WHITEBOARD_TILES) : [];

  return (
    <div className="pip-whiteboard-layout">
      <div className="pip-whiteboard-main">
        <WhiteboardReadOnly width="full" />
      </div>
      {showTiles && tileVideos.length > 0 && (
        <div className="pip-whiteboard-tiles">
          {tileVideos.map((video) => {
            const videoKey = getVideoKey(video);
            const isLeaving = leavingVideoIds.has(videoKey);
            return (
              <div key={videoKey} className={`pip-video-box ${isLeaving ? 'is-leaving' : ''}`}>
                <VideoBox {...video} isPipBox objectFit={getObjectFit(video.type)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export const PipLayerStack = memo(function PipLayerStack({
  showNormalLayer,
  showCompactLayer,
  normalLayerVisible,
  compactLayerVisible,
  normalContent,
  compactContent,
}: IPipLayerStackProps) {
  const normalLayerClass = `pip-layer pip-layer--normal ${normalLayerVisible ? 'is-visible' : ''}`;
  const compactLayerClass = `pip-layer pip-layer--compact ${compactLayerVisible ? 'is-visible' : ''}`;

  return (
    <div className="pip-layer-stack">
      {showNormalLayer && <div className={normalLayerClass}>{normalContent}</div>}
      {showCompactLayer && <div className={compactLayerClass}>{compactContent}</div>}
    </div>
  );
});
